import React, {FC, useEffect, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {MdAdd, MdInfo, MdList, MdTune} from 'react-icons/md';
import {Link as ReactLink} from 'react-router-dom';
import BodyContainer from '../../../components/BodyContainer';
import DatePicker from '../../../components/DatePicker';
import {useNotifications} from '../../../components/NotificationContext';
import PageHeader from '../../../components/PageHeader';
import PaymentAnalyticCard from '../../../components/PaymentAnalyticCard';
import TouchableHover from '../../../components/TouchableHover';
import useMe from '../../../hooks/useMe';
import usePermission from '../../../hooks/usePermission';
import LineGraph from '../../admin/components/LineGraph';
import BookingRequestsByDateAndStatus from '../components/BookingRequestsByDateAndStatus';
import ProviderOnboarding from '../components/ProviderOnboarding';
import QuickServiceBuilder from '../components/QuickServiceBuilder';
import TodaysBookingRequests from '../components/TodaysBookingRequests';
import {
	Box,
	Button,
	Checkbox,
	Heading,
	HStack,
	Icon,
	Menu,
	MenuButton,
	MenuList,
	Progress,
	Stack,
	Tag,
	Text,
	useDisclosure,
} from 'components/ui/chakra';
import {Glum} from 'puddleglum';
import LocationController from 'puddleglum/api/providers/LocationController';
import ProviderAnalyticsController from 'puddleglum/api/providers/ProviderAnalyticsController';
import {PaginatedResponse} from 'puddleglum/utils';

const ProviderPortalHomeScreen: FC = () => {
	const queryClient = useQueryClient();
	const [appointmentDate, setAppointmentDate] = useState(new Date());
	const [isReadOnly, setIsReadOnly] = useState<boolean>();
	const [includeRedeemedVouchers, setIncludeRedeemedVouchers] = useState(false);
	const [includeCanceledVouchers, setIncludeCanceledVouchers] = useState(false);
	const [includeCanceledBookingRequests, setIncludeCanceledBookingRequests] = useState(false);
	const {me} = useMe();
	const {registerCallback} = useNotifications();
	const permission = usePermission('providers');
	const servicePermission = usePermission('servicesAndPrices');
	const {
		isOpen: isQuickBuilderOpen,
		onOpen: openQuickBuilder,
		onClose: closeQuickBuilder,
	} = useDisclosure();
	const {data: locations, isLoading: areLocationsLoading} = useQuery<
		PaginatedResponse<Glum.Models.Location>
	>(['provider-home', 'locations'], async () => {
		const reply = await LocationController.index({
			page: 1,
			per_page: 1000,
			search: '',
		});

		return reply.data;
	});

	const {data: paymentsByMonth, refetch} = useQuery(['providerHomePaymentsByMonth'], async () => {
		const reply = await ProviderAnalyticsController.getPaymentsByMonth();
		return reply.data;
	});

	useEffect(() => {
		if (permission === 'all') {
			setIsReadOnly(false);
		}
		if (permission === 'read') {
			setIsReadOnly(true);
		}
		refetch();
	}, [permission, refetch]);

	useEffect(() => {
		if (me?.organization_id) {
			registerCallback(`organization.${me.organization_id}`, () => {
				queryClient.invalidateQueries(['bookingRequests']);
			});
		}
	}, [me?.organization_id, queryClient, registerCallback]);

	return (
		<>
			<PageHeader
				description='Stay on top of appointment flow, payments, and onboarding work.'
				eyebrow='Provider Portal'
				heading='Allora Platform'
			/>
			<BodyContainer mt={4} p={5}>
				<Stack spacing={6}>
					<Box maxW='4xl'>
						<Heading size='md'>Add & Edit Multiple Services in One Sheet</Heading>
						<Text color='gray.600' mt={3}>
							Search for a treatment or group, add it to a table, edit the display
							name, price, and locations inline, then save the batch once. Use the
							full editor later for advanced order requirements or patient-facing
							instructions.
						</Text>
					</Box>
					<Stack align='flex-start' spacing={4}>
						<HStack spacing={3} flexWrap='wrap' align='center'>
							{areLocationsLoading ? (
								<Progress size='xs' isIndeterminate w='12rem' />
							) : (
								<Tag colorScheme={locations?.data.length ? 'green' : 'orange'}>
									{locations?.data.length ?? 0} locations ready
								</Tag>
							)}
							<Tag colorScheme='purple'>Batch publish</Tag>
							<Tag colorScheme='teal'>Treatment + group support</Tag>
						</HStack>
						{(locations?.data.length ?? 0) === 0 && !areLocationsLoading && (
							<Box
								bg='orange.50'
								border='1px solid'
								borderColor='orange.200'
								borderRadius='lg'
								p={4}
								w='full'
							>
								<Box
									display={{base: 'block', sm: 'grid'}}
									gridTemplateColumns={{sm: 'minmax(0, 1fr) auto'}}
									alignItems='center'
									columnGap={3}
									rowGap={3}
								>
									<Stack spacing={1}>
										<Text color='orange.700' fontWeight='semibold'>
											Add a location first so you can save and publish
											services.
										</Text>
										<Text color='orange.700' fontSize='sm'>
											You can still stage work in Quick Builder and edit
											services and prices, but the save step needs at least
											one location.
										</Text>
									</Stack>
									<Button
										as={ReactLink}
										colorScheme='blue'
										to='/locations'
										size='sm'
										w={{base: 'full', sm: 'auto'}}
										alignSelf='center'
										justifySelf='center'
									>
										Add Locations
									</Button>
								</Box>
							</Box>
						)}
						<HStack spacing={3} flexWrap='wrap' align='center'>
							<Button
								colorScheme='blue'
								leftIcon={<MdAdd />}
								size='md'
								fontWeight='600'
								onClick={openQuickBuilder}
								isDisabled={isReadOnly || servicePermission === 'read'}
								w={{base: 'full', sm: 'auto'}}
							>
								Open Quick Builder
							</Button>
							<Button
								as={ReactLink}
								to='/services-and-prices'
								colorScheme='blue'
								leftIcon={<MdList />}
								size='md'
								fontWeight='600'
								variant='ghost'
								w={{base: 'full', sm: 'auto'}}
							>
								Open Full Editor
							</Button>
						</HStack>
					</Stack>
				</Stack>
			</BodyContainer>
			<HStack w='auto' alignItems='stretch' mt={4} spacing={4}>
				<Box flex={1}>
					<BodyContainer border='solid 1px' flex={2} p={4} mb={4}>
						<HStack justify='space-between' align='center' mb={2} gap={3}>
							<Heading size='md' color='gray.700'>
								Check-In Lookup
							</Heading>
							<Menu closeOnSelect={false}>
								<MenuButton asChild>
									<Button
										alignSelf='flex-start'
										leftIcon={<MdTune />}
										size='xs'
										variant='ghost'
									>
										Filter
									</Button>
								</MenuButton>
								<MenuList minW='16rem'>
									<Box px={2} py={1}>
										<Text
											fontSize='xs'
											fontWeight='600'
											color='gray.500'
											textTransform='uppercase'
										>
											Booking request search
										</Text>
									</Box>
									<Box px={2} py={1}>
										<Checkbox
											isChecked={includeCanceledBookingRequests}
											onChange={(event) =>
												setIncludeCanceledBookingRequests(
													event.target.checked,
												)
											}
										>
											Include canceled booking requests
										</Checkbox>
									</Box>
									<Box px={2} py={1}>
										<Text
											fontSize='xs'
											fontWeight='600'
											color='gray.500'
											textTransform='uppercase'
										>
											Voucher search
										</Text>
									</Box>
									<Box px={2} py={1}>
										<Checkbox
											isChecked={includeCanceledVouchers}
											onChange={(event) =>
												setIncludeCanceledVouchers(event.target.checked)
											}
										>
											Include canceled vouchers
										</Checkbox>
									</Box>
									<Box px={2} py={1}>
										<Checkbox
											isChecked={includeRedeemedVouchers}
											onChange={(event) =>
												setIncludeRedeemedVouchers(event.target.checked)
											}
										>
											Include redeemed vouchers
										</Checkbox>
									</Box>
								</MenuList>
							</Menu>
						</HStack>
						<BookingRequestsByDateAndStatus
							disabled={isReadOnly}
							includeRedeemedVouchers={includeRedeemedVouchers}
							includeCanceledVouchers={includeCanceledVouchers}
							includeCanceledBookingRequests={includeCanceledBookingRequests}
						/>
					</BodyContainer>
					<BodyContainer border='solid 1px' flex={2} p={4} mb={4}>
						<Stack
							w='full'
							direction={{base: 'column', lg: 'row'}}
							justifyContent='space-between'
							align={{base: 'stretch', lg: 'center'}}
							gap={4}
							mb={4}
						>
							<Box flex={1} minW={0}>
								<Heading size='md' color='gray.700'>
									Appointments
								</Heading>
							</Box>
							<Box w={{base: 'full', sm: '14.5rem'}} flexShrink={0}>
								<DatePicker
									value={appointmentDate}
									onChange={setAppointmentDate}
									size='md'
									width='100%'
								/>
							</Box>
						</Stack>
						<TodaysBookingRequests date={appointmentDate} disabled={isReadOnly} />
					</BodyContainer>

					<BodyContainer border='solid 1px' flex={2} p={4} mb={4}>
						<Heading size='md' mb={4} color='gray.700'>
							Payments by Month
						</Heading>
						<LineGraph
							data={paymentsByMonth as any[]}
							xAxisKey='date'
							lines={[
								{
									dataKey: 'total',
									label: 'Total Payments in USD',
									color: '#319795',
								},
							]}
							w='auto'
							h='400px'
							p={4}
							border='1px solid'
							borderColor='gray.100'
							borderRadius='md'
						/>
					</BodyContainer>
				</Box>
				<Box w='400px'>
					<BodyContainer border='solid 1px' flex={2} p={4}>
						<HStack w='full' justifyContent='space-between'>
							<Heading size='md' color='gray.700'>
								Onboarding
							</Heading>
						</HStack>
						<ProviderOnboarding />
					</BodyContainer>
					<BodyContainer border='solid 1px' flex={2} p={4} mt={4}>
						<Heading size='md' color='gray.700'>
							Pending Payments
							<Box float='right'>
								<TouchableHover label='This amount represent unpaid billing statements. This amount should be paid to you soon!'>
									<span>
										<Icon as={MdInfo} color='gray.500' />
									</span>
								</TouchableHover>
							</Box>
						</Heading>
						<PaymentAnalyticCard type='pending' scope='organization' />
					</BodyContainer>
					<BodyContainer border='solid 1px' flex={2} p={4} mt={4}>
						<Heading size='md' color='gray.700'>
							Recent Payments
							<Box float='right'>
								<TouchableHover label='This is the amount you have been paid by Allora in the past 30 days.'>
									<span>
										<Icon as={MdInfo} color='gray.500' />
									</span>
								</TouchableHover>
							</Box>
						</Heading>
						<PaymentAnalyticCard type='recent' scope='organization' />
					</BodyContainer>
					<BodyContainer border='solid 1px' flex={2} p={4} mt={4}>
						<Heading size='md' color='gray.700'>
							All Payments
							<Box float='right'>
								<TouchableHover label='This is the amount you have been by Allora for all time.'>
									<span>
										<Icon as={MdInfo} color='gray.500' />
									</span>
								</TouchableHover>
							</Box>
						</Heading>
						<PaymentAnalyticCard type='all' scope='organization' />
					</BodyContainer>
				</Box>
			</HStack>
			<QuickServiceBuilder isOpen={isQuickBuilderOpen} onClose={closeQuickBuilder} />
		</>
	);
};

export default ProviderPortalHomeScreen;
